import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { CalendarDays, Users, LogOut, Plus, Check, X, Clock, Edit2, Trash2, FileText, Phone, Mail, Building2, ChevronRight, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  planned: { label: 'Geplant', variant: 'outline', className: 'border-amber-400 text-amber-700 bg-amber-50' },
  valid: { label: 'Gultig', variant: 'outline', className: 'border-emerald-400 text-emerald-700 bg-emerald-50' },
  'no-show': { label: 'No-Show', variant: 'outline', className: 'border-red-400 text-red-700 bg-red-50' },
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

export default function Dashboard() {
  const { user: authUser, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const user = authUser || location.state?.user;

  const [appointments, setAppointments] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editAppointment, setEditAppointment] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const isAdmin = user?.role === 'admin';

  const fetchAppointments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/appointments`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAppointments(data);
      }
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPortalUsers(data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [isAdmin]);

  const fetchLeads = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_URL}/api/leads`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLeads(data);
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!authUser && !location.state?.user) return;
    if (!authUser && location.state?.user) {
      refreshUser();
    }
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAppointments(), fetchUsers(), fetchLeads()]);
      setLoading(false);
    };
    loadData();
  }, [authUser, location.state?.user, fetchAppointments, fetchUsers, fetchLeads, refreshUser]);

  const handleLogout = async () => {
    await logout();
    navigate('/portal', { replace: true });
  };

  const stats = {
    total: appointments.length,
    planned: appointments.filter(a => a.status === 'planned').length,
    valid: appointments.filter(a => a.status === 'valid').length,
    noshow: appointments.filter(a => a.status === 'no-show').length,
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="animate-spin h-8 w-8 border-2 border-[#0a0a0a] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="portal-dashboard">
      {/* Top Navigation */}
      <nav className="bg-[#0a0a0a] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <span className="text-white font-bold tracking-[0.15em] text-lg">VALORYX</span>
              <span className="text-white/30 text-sm hidden sm:inline">|</span>
              <span className="text-white/50 text-sm hidden sm:inline">Kundenportal</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {user.picture && (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                )}
                <div className="hidden sm:block text-right">
                  <p className="text-white text-sm font-medium">{user.name}</p>
                  <p className="text-white/40 text-xs">{isAdmin ? 'Admin' : user.company_name || 'Kunde'}</p>
                </div>
              </div>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white/60 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Gesamt</p>
                  <p className="text-2xl font-bold text-[#0a0a0a] mt-1" data-testid="stat-total">{stats.total}</p>
                </div>
                <CalendarDays className="h-8 w-8 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Geplant</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="stat-planned">{stats.planned}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Gultig</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="stat-valid">{stats.valid}</p>
                </div>
                <Check className="h-8 w-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">No-Show</p>
                  <p className="text-2xl font-bold text-red-600 mt-1" data-testid="stat-noshow">{stats.noshow}</p>
                </div>
                <X className="h-8 w-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Admin / Simple view for Customer */}
        {isAdmin ? (
          <Tabs defaultValue="appointments" className="space-y-6">
            <div className="flex items-center justify-between">
              <TabsList className="bg-white shadow-sm border">
                <TabsTrigger value="appointments" data-testid="tab-appointments" className="data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white">
                  <CalendarDays className="h-4 w-4 mr-2" /> Termine
                </TabsTrigger>
                <TabsTrigger value="leads" data-testid="tab-leads" className="data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white">
                  <FileText className="h-4 w-4 mr-2" /> Leads
                  {leads.length > 0 && <Badge className="ml-2 bg-[#3FA7DB] text-white text-[10px] px-1.5 py-0">{leads.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white">
                  <Users className="h-4 w-4 mr-2" /> Benutzer
                </TabsTrigger>
              </TabsList>
              <Button
                data-testid="create-appointment-btn"
                onClick={() => setCreateOpen(true)}
                className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-lg"
              >
                <Plus className="h-4 w-4 mr-2" /> Neuer Termin
              </Button>
            </div>

            <TabsContent value="appointments">
              <AppointmentsTable
                appointments={appointments}
                loading={loading}
                isAdmin={isAdmin}
                onEdit={(apt) => { setEditAppointment(apt); setEditOpen(true); }}
                onDelete={async (id) => {
                  if (!window.confirm('Termin wirklich loschen?')) return;
                  const res = await fetch(`${API_URL}/api/appointments/${id}`, { method: 'DELETE', credentials: 'include' });
                  if (res.ok) { toast.success('Termin geloscht'); fetchAppointments(); }
                  else toast.error('Fehler beim Loschen');
                }}
              />
            </TabsContent>

            <TabsContent value="leads">
              <LeadsTable leads={leads} loading={loading} onStatusChange={async (leadId, newStatus) => {
                try {
                  const res = await fetch(`${API_URL}/api/leads/${leadId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ lead_status: newStatus }),
                  });
                  if (res.ok) {
                    toast.success('Status aktualisiert');
                    fetchLeads();
                  } else {
                    toast.error('Fehler beim Aktualisieren');
                  }
                } catch {
                  toast.error('Netzwerkfehler');
                }
              }} />
            </TabsContent>

            <TabsContent value="users">
              <UserManagement
                users={portalUsers}
                loading={loading}
                onEdit={(u) => { setEditingUser(u); setEditUserOpen(true); }}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div>
            <h2 className="text-lg font-semibold text-[#0a0a0a] mb-4">Ihre Termine</h2>
            {!user.company_name ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500">Ihr Konto wurde noch keiner Firma zugeordnet.</p>
                  <p className="text-gray-400 text-sm mt-1">Bitte kontaktieren Sie VALORYX, um Zugang zu erhalten.</p>
                </CardContent>
              </Card>
            ) : (
              <AppointmentsTable
                appointments={appointments}
                loading={loading}
                isAdmin={false}
                onEdit={(apt) => { setEditAppointment(apt); setEditOpen(true); }}
              />
            )}
          </div>
        )}
      </main>

      {/* Create Appointment Dialog */}
      <CreateAppointmentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => { fetchAppointments(); setCreateOpen(false); }}
      />

      {/* Edit Appointment Dialog */}
      <EditAppointmentDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        appointment={editAppointment}
        isAdmin={isAdmin}
        onUpdated={() => { fetchAppointments(); setEditOpen(false); }}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editUserOpen}
        onOpenChange={setEditUserOpen}
        userToEdit={editingUser}
        onUpdated={() => { fetchUsers(); setEditUserOpen(false); }}
      />
    </div>
  );
}


function AppointmentsTable({ appointments, loading, isAdmin, onEdit, onDelete }) {
  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  if (appointments.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <CalendarDays className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Keine Termine vorhanden</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <Table data-testid="appointments-table">
        <TableHeader>
          <TableRow className="bg-gray-50/80">
            <TableHead className="font-semibold text-gray-600">Datum</TableHead>
            <TableHead className="font-semibold text-gray-600">Firma</TableHead>
            <TableHead className="font-semibold text-gray-600">Ansprechpartner</TableHead>
            <TableHead className="font-semibold text-gray-600">Status</TableHead>
            <TableHead className="font-semibold text-gray-600">Kommentar</TableHead>
            <TableHead className="font-semibold text-gray-600 text-right">Aktionen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.map((apt) => {
            const statusCfg = STATUS_CONFIG[apt.status] || STATUS_CONFIG.planned;
            return (
              <TableRow key={apt.id} data-testid={`appointment-row-${apt.id}`} className="hover:bg-gray-50/50">
                <TableCell className="font-medium text-sm">{formatDate(apt.appointment_date)}</TableCell>
                <TableCell className="text-sm">{apt.company_name}</TableCell>
                <TableCell className="text-sm text-gray-600">{apt.contact_person || '-'}</TableCell>
                <TableCell>
                  <Badge className={`${statusCfg.className} text-xs font-medium`} data-testid={`status-badge-${apt.id}`}>
                    {statusCfg.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-gray-600 max-w-[200px] truncate">{apt.comments || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`edit-appointment-${apt.id}`}
                      onClick={() => onEdit(apt)}
                      className="h-8 w-8 p-0 text-gray-500 hover:text-[#0a0a0a]"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {isAdmin && onDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-appointment-${apt.id}`}
                        onClick={() => onDelete(apt.id)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}


function CreateAppointmentDialog({ open, onOpenChange, onCreated }) {
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyName || !appointmentDate) {
      toast.error('Firma und Datum sind Pflichtfelder');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_name: companyName,
          contact_person: contactPerson || null,
          appointment_date: new Date(appointmentDate).toISOString(),
          notes: notes || null,
        }),
      });
      if (res.ok) {
        toast.success('Termin erstellt');
        setCompanyName(''); setContactPerson(''); setAppointmentDate(''); setNotes('');
        onCreated();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Erstellen');
      }
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-appointment-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Neuer Termin</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="company">Firma *</Label>
            <Input
              id="company"
              data-testid="input-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="z.B. aixpedIT GmbH"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="contact">Ansprechpartner</Label>
            <Input
              id="contact"
              data-testid="input-contact"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              placeholder="z.B. Max Mustermann"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="date">Datum & Uhrzeit *</Label>
            <Input
              id="date"
              data-testid="input-date"
              type="datetime-local"
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              data-testid="input-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Interne Notizen..."
              className="mt-1"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button
              type="submit"
              data-testid="submit-appointment-btn"
              disabled={submitting}
              className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white"
            >
              {submitting ? 'Erstelle...' : 'Termin erstellen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EditAppointmentDialog({ open, onOpenChange, appointment, isAdmin, onUpdated }) {
  const [status, setStatus] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (appointment) {
      setStatus(appointment.status || 'planned');
      setComments(appointment.comments || '');
    }
  }, [appointment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appointment) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, comments: comments || null }),
      });
      if (res.ok) {
        toast.success('Termin aktualisiert');
        onUpdated();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Aktualisieren');
      }
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setSubmitting(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-appointment-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Termin bearbeiten</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 text-sm text-gray-500 mt-1">
          <p><span className="font-medium text-gray-700">Firma:</span> {appointment.company_name}</p>
          <p><span className="font-medium text-gray-700">Datum:</span> {formatDate(appointment.appointment_date)}</p>
          {appointment.contact_person && (
            <p><span className="font-medium text-gray-700">Ansprechpartner:</span> {appointment.contact_person}</p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1" data-testid="select-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="planned">Geplant</SelectItem>
                <SelectItem value="valid">Gultig</SelectItem>
                <SelectItem value="no-show">No-Show</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="edit-comments">Kommentar</Label>
            <Textarea
              id="edit-comments"
              data-testid="input-edit-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Kommentar hinzufugen..."
              className="mt-1"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button
              type="submit"
              data-testid="submit-edit-btn"
              disabled={submitting}
              className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white"
            >
              {submitting ? 'Speichere...' : 'Speichern'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function UserManagement({ users, loading, onEdit }) {
  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  if (users.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Keine Benutzer vorhanden</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <Table data-testid="users-table">
        <TableHeader>
          <TableRow className="bg-gray-50/80">
            <TableHead className="font-semibold text-gray-600">Name</TableHead>
            <TableHead className="font-semibold text-gray-600">E-Mail</TableHead>
            <TableHead className="font-semibold text-gray-600">Rolle</TableHead>
            <TableHead className="font-semibold text-gray-600">Firma</TableHead>
            <TableHead className="font-semibold text-gray-600 text-right">Aktionen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`} className="hover:bg-gray-50/50">
              <TableCell className="font-medium text-sm">
                <div className="flex items-center gap-2">
                  {u.picture && <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />}
                  {u.name}
                </div>
              </TableCell>
              <TableCell className="text-sm text-gray-600">{u.email}</TableCell>
              <TableCell>
                <Badge
                  className={u.role === 'admin'
                    ? 'border-blue-400 text-blue-700 bg-blue-50 text-xs'
                    : 'border-gray-300 text-gray-600 bg-gray-50 text-xs'}
                >
                  {u.role === 'admin' ? 'Admin' : 'Kunde'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-gray-600">{u.company_name || '-'}</TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`edit-user-${u.user_id}`}
                  onClick={() => onEdit(u)}
                  className="h-8 w-8 p-0 text-gray-500 hover:text-[#0a0a0a]"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}


const LEAD_STATUS_CONFIG = {
  offen: { label: 'Offen', className: 'border-gray-300 text-gray-600 bg-gray-50' },
  kunde: { label: 'Kunde', className: 'border-emerald-400 text-emerald-700 bg-emerald-50' },
  abgelehnt: { label: 'Abgelehnt', className: 'border-red-400 text-red-700 bg-red-50' },
  nicht_erreicht: { label: 'Nicht erreicht', className: 'border-amber-400 text-amber-700 bg-amber-50' },
};

function LeadsTable({ leads, loading, onStatusChange }) {
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  if (leads.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Noch keine Leads vorhanden</p>
        </CardContent>
      </Card>
    );
  }

  const filtered = leads.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    const name = `${l.kontakt?.name || ''} ${l.kontakt?.lastName || ''}`.toLowerCase();
    const email = (l.email || '').toLowerCase();
    const phone = (l.kontakt?.phone || '').toLowerCase();
    const branche = (l.branche || '').toLowerCase();
    return name.includes(s) || email.includes(s) || phone.includes(s) || branche.includes(s);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Suche nach Name, E-Mail, Telefon, Branche..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          data-testid="leads-search"
        />
        <span className="text-sm text-gray-400">{filtered.length} von {leads.length} Leads</span>
      </div>
      <Card className="border-0 shadow-sm overflow-hidden">
        <Table data-testid="leads-table">
          <TableHeader>
            <TableRow className="bg-gray-50/80">
              <TableHead className="font-semibold text-gray-600">Datum</TableHead>
              <TableHead className="font-semibold text-gray-600">Name</TableHead>
              <TableHead className="font-semibold text-gray-600">E-Mail</TableHead>
              <TableHead className="font-semibold text-gray-600">Telefon</TableHead>
              <TableHead className="font-semibold text-gray-600">Branche</TableHead>
              <TableHead className="font-semibold text-gray-600">Auftragswert</TableHead>
              <TableHead className="font-semibold text-gray-600">Status</TableHead>
              <TableHead className="font-semibold text-gray-600 w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onStatusChange={onStatusChange} onView={() => setSelectedLead(lead)} />
            ))}
          </TableBody>
        </Table>
      </Card>
      {selectedLead && <LeadDetailDialog lead={selectedLead} open={!!selectedLead} onOpenChange={(o) => !o && setSelectedLead(null)} onStatusChange={onStatusChange} />}
    </div>
  );
}

function LeadRow({ lead, onStatusChange, onView }) {
  const name = `${lead.kontakt?.name || ''} ${lead.kontakt?.lastName || ''}`.trim() || '-';
  const currentStatus = lead.lead_status || 'offen';

  const statusColors = {
    offen: 'bg-gray-100 text-gray-700 border-gray-300',
    kunde: 'bg-emerald-50 text-emerald-700 border-emerald-400',
    meeting: 'bg-blue-50 text-blue-700 border-blue-400',
    kein_fit: 'bg-red-50 text-red-700 border-red-400',
    nicht_erreicht: 'bg-amber-50 text-amber-700 border-amber-400',
  };

  return (
    <TableRow data-testid={`lead-row-${lead.id}`} className="hover:bg-gray-50/50 cursor-pointer" onClick={onView}>
      <TableCell className="text-sm text-gray-500">{formatDate(lead.submitted_at)}</TableCell>
      <TableCell className="font-medium text-sm">{name}</TableCell>
      <TableCell className="text-sm">
        <a href={`mailto:${lead.email}`} className="text-[#3FA7DB] hover:underline flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Mail className="h-3 w-3" />{lead.email || '-'}
        </a>
      </TableCell>
      <TableCell className="text-sm">
        {lead.kontakt?.phone ? (
          <a href={`tel:${lead.kontakt.phone}`} className="text-[#3FA7DB] hover:underline flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Phone className="h-3 w-3" />{lead.kontakt.phone}
          </a>
        ) : '-'}
      </TableCell>
      <TableCell className="text-sm text-gray-600">{lead.branche || '-'}</TableCell>
      <TableCell className="text-sm text-gray-600">{lead.auftragswert || '-'}</TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <select
          value={currentStatus}
          onChange={(e) => onStatusChange(lead.id, e.target.value)}
          data-testid={`lead-status-${lead.id}`}
          className={`text-xs font-medium px-3 py-1.5 rounded-md border cursor-pointer outline-none ${statusColors[currentStatus] || statusColors.offen}`}
        >
          <option value="offen">Offen</option>
          <option value="kunde">Kunde</option>
          <option value="meeting">Meeting scheduled</option>
          <option value="kein_fit">Kein Fit</option>
          <option value="nicht_erreicht">Nicht erreicht</option>
        </select>
      </TableCell>
      <TableCell>
        <Eye className="h-4 w-4 text-gray-400" />
      </TableCell>
    </TableRow>
  );
}

const ANSWER_LABELS = {
  ziel: 'Ziel',
  branche: 'Branche',
  auftragswert: 'Auftragswert',
  teamgroesse: 'Teamgröße',
  vertriebskanal: 'Aktuelle Kanäle',
  wunsch_termine: 'Gewünschte Termine/Monat',
  region: 'Zielmarkt',
  zeitrahmen: 'Wann starten?',
  erwartung: 'Wichtig bei Partner',
  // Legacy fields
  herausforderung: 'Herausforderung',
  aktuelle_termine: 'Aktuelle Termine/Monat',
};

function LeadDetailDialog({ lead, open, onOpenChange, onStatusChange }) {
  const name = `${lead.kontakt?.name || ''} ${lead.kontakt?.lastName || ''}`.trim() || '-';
  const currentStatus = lead.lead_status || 'offen';

  const statusColors = {
    offen: 'bg-gray-100 text-gray-700 border-gray-300',
    kunde: 'bg-emerald-50 text-emerald-700 border-emerald-400',
    meeting: 'bg-blue-50 text-blue-700 border-blue-400',
    kein_fit: 'bg-red-50 text-red-700 border-red-400',
    nicht_erreicht: 'bg-amber-50 text-amber-700 border-amber-400',
  };

  // Collect all answer fields (exclude meta fields)
  const metaKeys = ['id', 'email', 'kontakt', 'submitted_at', 'lead_status'];
  const answerEntries = Object.entries(lead).filter(([key]) => !metaKeys.includes(key) && lead[key]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 mt-2">
          {/* Contact info */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">E-Mail</p>
              <a href={`mailto:${lead.email}`} className="text-sm text-[#3FA7DB] hover:underline">{lead.email || '-'}</a>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Telefon</p>
              {lead.kontakt?.phone ? (
                <a href={`tel:${lead.kontakt.phone}`} className="text-sm text-[#3FA7DB] hover:underline">{lead.kontakt.phone}</a>
              ) : <span className="text-sm text-gray-400">-</span>}
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Datum</p>
              <p className="text-sm text-gray-700">{formatDate(lead.submitted_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Status</p>
              <select
                value={currentStatus}
                onChange={(e) => onStatusChange(lead.id, e.target.value)}
                className={`text-xs font-medium px-3 py-1.5 rounded-md border cursor-pointer outline-none ${statusColors[currentStatus] || statusColors.offen}`}
              >
                <option value="offen">Offen</option>
                <option value="kunde">Kunde</option>
                <option value="meeting">Meeting scheduled</option>
                <option value="kein_fit">Kein Fit</option>
                <option value="nicht_erreicht">Nicht erreicht</option>
              </select>
            </div>
          </div>

          {/* All answers */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Quiz-Antworten</h3>
            <div className="space-y-3">
              {answerEntries.map(([key, value]) => {
                const label = ANSWER_LABELS[key] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : String(value);
                return (
                  <div key={key} className="border-b border-gray-100 pb-3">
                    <p className="text-xs text-gray-500 font-semibold mb-0.5">{label}</p>
                    <p className="text-sm text-gray-900">{displayValue || '-'}</p>
                  </div>
                );
              })}
              {answerEntries.length === 0 && (
                <p className="text-sm text-gray-400">Keine Antworten vorhanden</p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function EditUserDialog({ open, onOpenChange, userToEdit, onUpdated }) {
  const [role, setRole] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (userToEdit) {
      setRole(userToEdit.role || 'customer');
      setCompanyName(userToEdit.company_name || '');
    }
  }, [userToEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userToEdit) return;
    setSubmitting(true);
    try {
      const body = {};
      if (role !== userToEdit.role) body.role = role;
      if (companyName !== (userToEdit.company_name || '')) body.company_name = companyName || null;

      if (Object.keys(body).length === 0) {
        toast.info('Keine Anderungen');
        onOpenChange(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/users/${userToEdit.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Benutzer aktualisiert');
        onUpdated();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Aktualisieren');
      }
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setSubmitting(false);
    }
  };

  if (!userToEdit) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Benutzer bearbeiten</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-500 mt-1">
          <p><span className="font-medium text-gray-700">{userToEdit.name}</span> ({userToEdit.email})</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Rolle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1" data-testid="select-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="customer">Kunde</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="user-company">Firma</Label>
            <Input
              id="user-company"
              data-testid="input-user-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="z.B. aixpedIT GmbH"
              className="mt-1"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button
              type="submit"
              data-testid="submit-user-edit-btn"
              disabled={submitting}
              className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white"
            >
              {submitting ? 'Speichere...' : 'Speichern'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
